/**
 * Admin Dashboard API Service
 * Handles all API calls for admin dashboard functionality
 */

import { raiseSessionExpired } from './sessionExpiry';

const API_BASE_URL = '/api';

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Handle API response and errors. Always throws on a non-OK response —
 * including 401, which clears the session and redirects to /login first and
 * then throws anyway, so a caller's ordinary catch block has something to
 * catch rather than a resolved `undefined` it has to remember to check for.
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      raiseSessionExpired();
    }

    const errorData = await response.json().catch(() => ({}));

    // Handle specific error cases
    if (response.status === 400) {
      throw new Error(errorData.message || 'Invalid request parameters');
    }
    if (response.status === 403) {
      throw new Error('Access denied - can_view_admin_dash permission required');
    }

    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get login statistics for specified number of days
 * @param {number} days - Number of days to look back (1-365)
 */
export const getLoginStats = async (days) => {
  try {
    if (!days || days < 1 || days > 365) {
      throw new Error('Days parameter must be between 1 and 365');
    }

    const response = await fetch(`${API_BASE_URL}/admin/stats/logins?days=${days}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error(`Failed to fetch ${days}-day login stats:`, error);
    throw error;
  }
};

/**
 * Get number of accounts logged in the last 7 days
 */
export const getLogins7Days = async () => {
  return getLoginStats(7);
};

/**
 * Get number of accounts logged in the last 30 days
 */
export const getLogins30Days = async () => {
  return getLoginStats(30);
};

/**
 * Get total number of accounts
 */
export const getTotalAccounts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/stats/accounts/total`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Failed to fetch total accounts:', error);
    throw error;
  }
};

/**
 * Get new account statistics for specified number of days
 * @param {number} days - Number of days to look back (1-365)
 */
export const getNewAccountStats = async (days) => {
  try {
    if (!days || days < 1 || days > 365) {
      throw new Error('Days parameter must be between 1 and 365');
    }

    const response = await fetch(`${API_BASE_URL}/admin/stats/accounts/new?days=${days}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error(`Failed to fetch ${days}-day new account stats:`, error);
    throw error;
  }
};

/**
 * Get number of accounts created in the last 7 days
 */
export const getNewAccounts7Days = async () => {
  return getNewAccountStats(7);
};

/**
 * Get paginated list of all user accounts
 */
export const getAccounts = async (params = {}) => {
  try {
    const {
      page = 1,
      page_size = 25,
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
      sort_by,
      sort_order
    });

    const response = await fetch(`${API_BASE_URL}/admin/accounts?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Failed to fetch accounts:', error);
    throw error;
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Never';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Matches a bare `YYYY-MM-DD` value with nothing else attached.
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date-only field for display, shared by every caller that shows a
 * calendar day rather than a moment in time (formatDate, above, covers the
 * latter).
 *
 * The field may arrive as either a bare `YYYY-MM-DD` string or a full
 * ISO/RFC3339 timestamp, and those two shapes must be parsed differently:
 * `new Date("1990-01-15")` parses as UTC midnight, which renders as the
 * previous day in any negative-UTC-offset timezone, so a bare date-only
 * value is anchored to local midnight before formatting. A full timestamp
 * already carries a time and zone, so it is parsed as-is — anchoring it too
 * would be wrong.
 *
 * Returns null when the value is missing or does not parse, so each call
 * site supplies its own fallback text ('Not provided', '', 'Subscriber', ...)
 * rather than this helper picking one for all of them.
 */
export const formatDateOnly = (value) => {
  if (!value) return null;

  const date = DATE_ONLY_PATTERN.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Format numbers with commas
 */
export const formatNumber = (num) => {
  if (typeof num !== 'number') return '0';
  return num.toLocaleString();
};